import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00d4ff',
      light: '#33ddff',
      dark: '#00a8cc',
      contrastText: '#000000',
    },
    secondary: {
      main: '#ff2e63',
      light: '#ff5e88',
      dark: '#cc0040',
    },
    background: {
      default: '#0a0e17',
      paper: '#1a2332',  // Lighter cards for better contrast
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b8d0',
    },
    success: {
      main: '#00ff88',
      dark: '#00cc6a',
    },
    warning: {
      main: '#ffaa00',
    },
    error: {
      main: '#ff2e63',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { 
      fontWeight: 800, 
      fontSize: '3rem',
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: { 
      fontWeight: 700, 
      fontSize: '2.5rem',
      letterSpacing: '-0.01em',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: { 
      fontWeight: 700, 
      fontSize: '2rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: { 
      fontWeight: 600, 
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1.125rem' },
    body1: { fontSize: '1rem', lineHeight: 1.7 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    subtitle1: { fontWeight: 500, fontSize: '1.125rem', opacity: 0.95 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
  },
  shape: { borderRadius: 4 },  // Sharp corners, modern look
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.3)',
    '0 4px 8px rgba(0,0,0,0.35)',
    '0 8px 16px rgba(0,0,0,0.4)',
    '0 12px 24px rgba(0,0,0,0.45)',
    '0 16px 32px rgba(0,0,0,0.5)',
    '0 20px 40px rgba(0,0,0,0.55)',
    '0 24px 48px rgba(0,0,0,0.6)',
    ...Array(17).fill('0 24px 48px rgba(0,0,0,0.6)'),
  ],
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',  // Remove default gradient
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.45)',
        },
        elevation3: {
          boxShadow: '0 8px 16px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.45)',
          transition: 'box-shadow 0.3s, transform 0.3s',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,212,255,0.3)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { 
          borderRadius: 4,  // Sharp corners
          padding: '10px 24px',
          minHeight: '44px',  // Minimum touch target for mobile
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '@media (max-width:600px)': {
            padding: '12px 20px',  // Larger padding on mobile
            fontSize: '0.9375rem',
          },
        },
        sizeLarge: {
          minHeight: '48px',  // Even larger for prominent buttons
          '@media (max-width:600px)': {
            minHeight: '52px',
            fontSize: '1rem',
          },
        },
        containedPrimary: {
          backgroundColor: '#00d4ff',
          boxShadow: '0 4px 12px rgba(0,212,255,0.4)',
          '&:hover': { 
            backgroundColor: '#00b8ff',
            boxShadow: '0 6px 16px rgba(0,212,255,0.5)',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: 'rgba(0,212,255,0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            '& .MuiInputBase-input': {
              fontSize: '16px',  // Prevents zoom on iOS
              padding: '16px 14px',  // Larger touch target
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            fontSize: '0.75rem',
            height: '28px',
          },
        },
      },
    },
    // Make text on hero background super readable
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.hero-title': {
            color: '#ffffff',
            textShadow: '0 4px 12px rgba(0,0,0,0.6)',
            fontWeight: 800,
          },
          '&.hero-subtitle': {
            color: '#e0f7ff',
            textShadow: '0 2px 8px rgba(0,0,0,0.5)',
          },
        },
      },
    },
  },
});

export default theme;
